import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DRIVE_FOLDER_SEARCH_TYPE, GROUPING_VIEW_TYPE } from "@constants";
import {
    createDriveFolder,
    getFoldersList,
    updateFolderState,
    updateSelectedFolder,
    updateFolderInfo,
} from "@modules/folderTreeView";
import { setNotification } from "@modules/notification";
import { useTranslation } from "react-i18next";
import store from "@store/store";
import axios from "axios";
import useVirtualFolderPath from "@hooks/useVirtualFolderPath";

const useFolder = ({ domain, driveInfo }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { getVirtualFolderFullPathList, getVirtualFolder } = useVirtualFolderPath({ domain, driveInfo });

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const folderTreeView = useSelector(({ folderTreeView }) => folderTreeView);

    const [currentFolderInfo, setCurrentFolderInfo] = useState(null);

    const [abortSource, setAbortSource] = useState(axios.CancelToken.source());

    const onClickFolderTree = async (
        item,
        isClickArrow,
        useNextToken,
        checkUseHistory = false,
        onClickTreeItem = () => {},
    ) => {
        try {
            const folderTreeView = store.getState().folderTreeView; //Note: TreeViewLayout Mount 직후 호출 시 driveList, folderList가 갱신되지 않은 상태임
            if (!folderTreeView.driveList.data?.length || !folderTreeView.folderList.data[item.id]) return false;

            if (!isClickArrow || folderTreeView.folderList.data[item.id].isFold) {
                const queryParameters = {
                    parentId: item.id,
                    searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
                    nextToken: useNextToken ? item.nextToken : undefined,
                };
                const headerParams = { projectId: project.id };
                const newAbortSource = axios.CancelToken.source();

                setAbortSource(newAbortSource);

                dispatch(
                    getFoldersList({
                        driveId: item.driveId,
                        queryParameters,
                        headerParams,
                        abortToken: newAbortSource.token,
                    }),
                ); //TODO: 드라이브가 여러개일 때 driveId 구할 수 있는 방법을 찾아야함

                if (!isClickArrow) {
                    onClickTreeItem({
                        id: item.id,
                        driveId: item.driveId,
                        hierarchyType: GROUPING_VIEW_TYPE.FOLDER,
                    });
                    if (!checkUseHistory) dispatch(updateSelectedFolder(item));
                }
            } else {
                dispatch(
                    updateFolderState({
                        [item.id]: { ...folderTreeView.folderList.data[item.id], isFold: true },
                    }),
                );
                abortSource.cancel();
            }
            Promise.resolve();
        } catch (error) {
            console.error(error);
            Promise.reject(error);
        }
    };

    const onClickCreateNewFolder = async (name, folder) => {
        try {
            const params = { name, parent: { id: folder.id } };
            const driveId = folder.driveId;

            await dispatch(createDriveFolder(driveId, params));
            const queryParameters = {
                parentId: folder.id,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
            };
            const headerParams = { projectId: project.id };
            await dispatch(
                getFoldersList({
                    driveId,
                    queryParameters,
                    headerParams,
                }),
            );
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const onRenameFolder = async (item, name) => {
        try {
            const params = { name };

            const queryParameters = {
                parentId: item.parent,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
            };
            const headerParams = { projectId: project.id };

            await dispatch(updateFolderInfo({ driveId: item.driveId, folderId: item.id }, params));
            await dispatch(
                getFoldersList({
                    driveId: item.driveId,
                    queryParameters,
                    headerParams,
                }),
            );
        } catch (error) {
            dispatch(
                setNotification({
                    type: "error",
                    contents:
                        t(`common::label::${"Error renaming from {{item1}} to {{item2}}."}`, {
                            item1: item.name,
                            item2: name,
                        }) +
                        "- " +
                        error?.data?.message,
                }),
            );

            return Promise.reject(error);
        }
    };

    useEffect(async () => {
        const folder = folderTreeView.folderList.selectedFolder?.folder;
        if (!domain) return null;
        if (!folder) {
            const rootFolder = await getVirtualFolder({});
            const { id, name, depth } = rootFolder?.folderPath || {};
            setCurrentFolderInfo({
                folderId: id,
                folderPath: [{ id, name, depth }],
            });
        } else {
            const folderPathList = await getVirtualFolderFullPathList({
                folderId: folder?.id,
            });

            setCurrentFolderInfo({
                folderId: folder?.id,
                folderPath: folderPathList
                    ? [...folderPathList, { id: folder?.id, name: folder?.name, depth: folderPathList.length }]
                    : [],
            });
        }
    }, [folderTreeView.folderList.selectedFolder?.folder]);
    return {
        onRenameFolder,
        onClickFolderTree,
        onClickCreateNewFolder,
        currentFolderInfo,
    };
};

export default useFolder;
